import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import DashboardShell from '../../components/layout/DashboardShell';
import CreatorFeedbackPostCard from '../../components/creatorFeedback/CreatorFeedbackPostCard';
import AskFeedbackModal from '../../components/creatorFeedback/AskFeedbackModal';
import LoggedInCreatorPreviewCard from '../../components/creatorFeedback/LoggedInCreatorPreviewCard';
import { subscribeFeedbackPosts } from '../../lib/feedbackPosts/firestore';
import { useCurrentInfluencerProfile } from '../../hooks/useCurrentInfluencerProfile';
import { triggerProfilePropagation } from '../../lib/profile/triggerProfilePropagation';

export default function FeedbackFeedPage({ onNavigate, initialExpandPostId }) {
  const { user } = useAuth();
  const { influencerProfile } = useCurrentInfluencerProfile();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAskModalOpen, setIsAskModalOpen] = useState(false);
  const [expandedSet, setExpandedSet] = useState(() => new Set());
  const [showAllCommentsSet, setShowAllCommentsSet] = useState(() => new Set());
  const expandedCardRef = useRef(null);
  const didAutoPropagateRef = useRef(false);

  const toggleExpanded = (postId) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const toggleShowAllComments = (postId) => {
    setShowAllCommentsSet((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };

  const expandPost = (postId) => {
    setExpandedSet((prev) => new Set(prev).add(postId));
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const unsub = subscribeFeedbackPosts(
      50,
      (list) => {
        setPosts(list);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Something went wrong.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!initialExpandPostId || loading || posts.length === 0) return;
    const hasPost = posts.some((p) => p.id === initialExpandPostId);
    if (hasPost) {
      setExpandedSet((prev) => new Set(prev).add(initialExpandPostId));
    }
  }, [initialExpandPostId, loading, posts]);

  // Self-heal: if your own posts still have stale denormalized author fields,
  // trigger propagation once when the feed loads.
  useEffect(() => {
    if (didAutoPropagateRef.current) return;
    if (!user?.uid) return;
    if (loading) return;
    if (!influencerProfile) return;
    if (!posts || posts.length === 0) return;

    const currentDisplayName = (
      influencerProfile?.displayName ||
      influencerProfile?.identity?.displayName ||
      user.displayName ||
      ''
    ).trim();
    if (!currentDisplayName) return;

    const hasStale = posts.some(
      (p) =>
        p.authorId === user.uid &&
        String(p.authorDisplayName || '').trim() !== currentDisplayName
    );

    if (!hasStale) return;

    didAutoPropagateRef.current = true;
    triggerProfilePropagation({
      uid: user.uid,
      getToken: () => (user ? user.getIdToken() : Promise.resolve(null)),
    }).catch(() => {});
  }, [user, loading, influencerProfile, posts]);

  useEffect(() => {
    if (!initialExpandPostId || !expandedSet.has(initialExpandPostId)) return;
    const el = expandedCardRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      const t = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [initialExpandPostId, expandedSet]);

  const stats = useMemo(() => {
    const open = posts.filter((p) => p.status === 'open');
    const needsReply = open.filter((p) => p.commentCount === 0).length;
    return {
      total: posts.length,
      openCount: open.length,
      needsReplyCount: needsReply,
    };
  }, [posts]);

  return (
    <DashboardShell onNavigate={onNavigate}>
      <div className="min-h-[55vh] w-full min-w-0">
        <div className="mx-auto w-full max-w-6xl min-w-0 px-3 pb-6 sm:px-6 lg:px-8">
          <div className="flex w-full min-w-0 flex-col gap-6 lg:flex-row lg:items-start lg:gap-10">
            {/* Logged-in creator preview: stacked on mobile, left sidebar on desktop */}
            <div className="order-1 w-full min-w-0 shrink-0 lg:order-none lg:w-[272px] lg:pt-1">
              <div className="lg:sticky lg:top-24">
                <LoggedInCreatorPreviewCard posts={posts} onNavigate={onNavigate} />
              </div>
            </div>

            <div className="order-2 w-full min-w-0 flex-1 lg:order-none">
              <div className="mx-auto w-full max-w-2xl min-w-0">
          {/* Workspace header */}
          <header className="mb-6 rounded-xl border border-slate-200/90 bg-white px-4 py-5 sm:px-5 sm:py-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                  Feedback
                </h1>
                <p className="mt-1.5 text-sm text-slate-600 leading-snug">
                  Open requests from creators looking for feedback. Ask a specific question and get input from other creators. These feedback posts will not be shown to brands searching for influencers.
                </p>
                {!loading && posts.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    {stats.openCount === 0 ? (
                      <span>No open requests right now. Ask for feedback to start one.</span>
                    ) : (
                      <>
                        <span>
                          <span className="font-semibold text-slate-700">{stats.openCount}</span>
                          {' '}
                          {stats.openCount === 1 ? 'open request' : 'open requests'}
                        </span>
                        {stats.needsReplyCount > 0 && (
                          <>
                            <span aria-hidden className="text-slate-300">
                              ·
                            </span>
                            <span className="text-amber-800/90">
                              <span className="font-semibold">{stats.needsReplyCount}</span>
                              {' '}
                              {stats.needsReplyCount === 1 ? 'needs' : 'need'} a reply
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsAskModalOpen(true)}
                className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center rounded-lg bg-ihTeal px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-ihTeal-dark focus:outline-none focus:ring-2 focus:ring-ihTeal focus:ring-offset-2 transition-colors active:scale-[0.99] sm:self-start"
              >
                Ask for feedback
              </button>
            </div>
          </header>

          <AskFeedbackModal
            isOpen={isAskModalOpen}
            onClose={() => setIsAskModalOpen(false)}
            onSuccess={(postId) => {
              setIsAskModalOpen(false);
              expandPost(postId);
            }}
          />

          {loading && (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
              <div className="spinner mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600">Loading…</p>
            </div>
          )}

          {error && !loading && (
            <div
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm font-medium text-red-800"
              role="alert"
            >
              {error}
            </div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-center shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Nothing here yet
              </p>
              <h2 className="mt-2 text-lg font-semibold text-slate-900">
                Start the first feedback request
              </h2>
              <p className="mt-2 text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">
                Post a hook, caption, or idea you want another creator to look at. Someone will jump in when they can help.
              </p>
              <button
                type="button"
                onClick={() => setIsAskModalOpen(true)}
                className="mt-6 inline-flex items-center justify-center rounded-lg bg-ihTeal px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-ihTeal-dark focus:outline-none focus:ring-2 focus:ring-ihTeal focus:ring-offset-2"
              >
                Ask for feedback
              </button>
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <section aria-labelledby="feedback-feed-heading">
              <div className="mb-3 flex items-baseline justify-between gap-3">
                <h2
                  id="feedback-feed-heading"
                  className="text-xs font-semibold uppercase tracking-wider text-slate-500"
                >
                  Recent activity
                </h2>
              </div>
              <ul className="flex flex-col gap-3 pb-10">
                {posts.map((post) => (
                  <li
                    key={post.id}
                    ref={post.id === initialExpandPostId ? expandedCardRef : undefined}
                  >
                    <CreatorFeedbackPostCard
                      post={post}
                      isExpanded={expandedSet.has(post.id)}
                      onToggleExpand={() => toggleExpanded(post.id)}
                      onNavigate={onNavigate}
                      showAllComments={showAllCommentsSet.has(post.id)}
                      onToggleShowAllComments={() => toggleShowAllComments(post.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
